import Image from 'next/image';
import Link from 'next/link';
import { format, parseISO } from 'date-fns';
import type { TeamPackage } from '@v2/services/team-schedule';
import { Building05, Check } from '@v2/ui/Icon/General';
import { Ticket01 } from '@v2/ui/Icon/MapsAndTravel';
import { formatTeamPrice } from '../util/teamSchedule';

export interface TeamFeaturedPackage extends TeamPackage {
  imageUrl: string;
  href: string;
}

interface Props {
  teamName: string;
  packages: TeamFeaturedPackage[];
}

export function TeamFeaturedPackages({ teamName, packages }: Props) {
  if (!packages.length) return null;

  return (
    <section aria-labelledby="team-featured-packages-title">
      <div className="mb-5 flex items-start justify-between gap-4 md:items-center">
        <h2
          id="team-featured-packages-title"
          className="text-xl font-bold text-gray-800 md:text-2xl"
        >
          Featured {teamName} Packages
        </h2>
        <Link
          href="#events"
          className="hidden shrink-0 text-sm font-semibold text-primary-500 hover:text-primary-600 md:block"
        >
          Browse all games
        </Link>
      </div>
      <ul className="no-scrollbar -mx-4 flex snap-x snap-mandatory scroll-px-4 gap-5 overflow-x-auto px-4 pb-2 md:mx-0 md:grid md:grid-cols-3 md:gap-4 md:overflow-visible md:px-0 md:pb-0">
        {packages.map((item) => {
          const eventDate = parseISO(item.occursAtLocal.slice(0, 10));
          const hasValidDate = Number.isFinite(eventDate.getTime());
          const { perTraveler, currency } = item.price;

          return (
            <li
              key={item.eventId}
              className="w-[85%] max-w-[340px] shrink-0 snap-start overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm md:w-auto md:max-w-none"
            >
              <Link
                href={item.href}
                className="flex h-full flex-col focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-500"
              >
                <div className="relative aspect-video shrink-0 bg-gray-100">
                  <Image
                    src={item.imageUrl}
                    alt=""
                    fill
                    sizes="(max-width: 399px) 85vw, (max-width: 767px) 340px, (max-width: 1359px) 33vw, 416px"
                    className="object-cover"
                  />
                  <time
                    dateTime={item.occursAtLocal}
                    className="absolute left-3 top-3 flex w-20 flex-col items-center overflow-hidden rounded-xl border border-gray-200 bg-white pt-1 text-gray-800 shadow-sm"
                  >
                    <span className="text-sm font-medium uppercase leading-6">
                      {hasValidDate
                        ? format(eventDate, 'MMM').toUpperCase()
                        : 'Date'}
                    </span>
                    <span className="text-3xl font-bold leading-9">
                      {hasValidDate ? format(eventDate, 'dd') : '--'}
                    </span>
                    <span className="mt-1 w-full bg-gray-50 py-1 text-center text-xs font-medium uppercase text-gray-500">
                      {hasValidDate
                        ? format(eventDate, 'EEE').toUpperCase()
                        : ' '}
                    </span>
                  </time>
                </div>
                <div className="grid flex-1 grid-cols-[minmax(0,1fr)_auto]">
                  <h3 className="col-span-2 min-h-24 p-4 text-xl font-semibold leading-8 text-gray-800 md:col-span-1 md:min-h-20 md:pr-0 md:text-base md:leading-6 lg:text-lg">
                    <span className="line-clamp-2">{item.name}</span>
                  </h3>
                  {/* Kept when unpriced: it holds the mobile footer border left of the button. */}
                  <p className="col-start-1 row-start-2 border-t border-gray-200 px-4 py-3 text-sm text-gray-600 md:col-start-2 md:row-start-1 md:border-0 md:text-right">
                    {perTraveler !== null && (
                      <>
                        <span className="block text-xs leading-5">
                          Starting at
                        </span>
                        <span className="block text-2xl font-bold leading-8 text-gray-800">
                          {formatTeamPrice(perTraveler, currency)}
                        </span>
                        <span className="block text-xs leading-5">
                          per traveler
                        </span>
                      </>
                    )}
                  </p>
                  {perTraveler !== null && (
                    <div className="col-span-2 hidden px-4 pb-4 md:block">
                      <p className="mb-2 text-xs text-gray-500">
                        Price includes:
                      </p>
                      <ul className="space-y-1.5 text-sm leading-5 text-gray-600">
                        <li className="flex items-start gap-2">
                          <Building05 size="16" className="mt-0.5 shrink-0" />
                          <span>
                            {item.includes.hotel.nights}{' '}
                            {item.includes.hotel.nights === 1
                              ? 'night'
                              : 'nights'}{' '}
                            at {item.includes.hotel.name}
                          </span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Ticket01 size="16" className="mt-0.5 shrink-0" />
                          <span>{item.includes.ticketTier} game ticket</span>
                        </li>
                        {item.includes.taxesAndFees && (
                          <li className="flex items-start gap-2">
                            <Check
                              size="16"
                              className="mt-0.5 shrink-0"
                              pathProps={{ stroke: 'currentColor' }}
                            />
                            <span>All taxes and fees</span>
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                  <span className="col-start-2 row-start-2 flex items-end justify-end border-t border-gray-200 py-3 pr-4 md:col-span-2 md:col-start-1 md:row-start-3 md:items-center md:justify-center md:p-3">
                    <span className="inline-flex min-h-12 items-center justify-center rounded-lg bg-primary-500 px-4 text-sm font-semibold text-white md:min-h-6 md:bg-transparent md:p-0 md:text-primary-500">
                      Plan Your Trip
                    </span>
                  </span>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
